import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { trackPageView } from '../utils/analytics';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiMail, FiClock, FiArrowLeft, FiFileText, FiUsers } = FiIcons;

const FreeLandingPageBRThankYou = () => {
  useEffect(() => {
    trackPageView('Free Landing Page BR Thank You');
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Back to home link */}
      <Link to="/" className="fixed top-4 left-4 z-50 bg-black-900/80 p-2 rounded-full hover:bg-black-900 transition-colors">
        <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-white" />
      </Link>

      {/* Confetti Effect */}
      <div className="fixed inset-0 pointer-events-none z-10">
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-gradient-to-r from-green-400 to-yellow-500 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: -10,
              rotate: 0,
              scale: Math.random() * 0.5 + 0.5
            }}
            animate={{
              y: window.innerHeight + 10,
              rotate: 360,
              scale: 0
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              delay: Math.random() * 2,
              ease: "easeOut"
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8"
          >
            <SafeIcon icon={FiCheck} className="w-12 h-12 text-white" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-6xl font-bold text-black-900 mb-6 font-serif"
          >
            Solicitação Recebida! 🇧🇷
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-black-700 mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Obrigado por se inscrever para sua landing page gratuita! Recebemos suas informações e estamos animados para criar algo incrível para seu negócio.
          </motion.p>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-black-900 mb-4 font-serif">O que acontece agora?</h2>
            <div className="w-16 h-1 bg-black-900 mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-black-50 p-6 rounded-lg text-center"
            >
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiMail} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Confirmação por Email</h3>
              <p className="text-black-700">Você receberá um email de confirmação nos próximos minutos.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              className="bg-black-50 p-6 rounded-lg text-center"
            >
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiFileText} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Questionário da Marca</h3>
              <p className="text-black-700">Complete o questionário sobre sua marca para nos ajudar a criar a página perfeita.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="bg-black-50 p-6 rounded-lg text-center"
            >
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiUsers} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Contato Pessoal</h3>
              <p className="text-black-700">Entraremos em contato em até 48 horas úteis para discutir os detalhes do seu projeto.</p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="bg-black-900 text-white p-8 rounded-lg text-center"
          >
            <h3 className="text-2xl font-bold mb-4">Prazo: 5-7 Dias Úteis</h3>
            <p className="text-lg text-black-200 mb-6">
              Sua landing page profissional e otimizada para mobile estará pronta em 5-7 dias úteis após recebermos todos os materiais necessários e alinharmos a direção.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link to="/free-landing-page-br">
                <button className="px-6 py-3 bg-white text-black-900 rounded-lg font-semibold hover:bg-black-100 transition-colors">
                  Saiba Mais Sobre Nosso Processo
                </button>
              </Link>
              <Link to="/">
                <button className="px-6 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-black-900 transition-colors">
                  Voltar ao Início
                </button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.6 }}
            className="mt-8 text-center"
          >
            <div className="flex items-center justify-center space-x-2 text-black-600">
              <SafeIcon icon={FiClock} className="w-5 h-5" />
              <span>Tempo esperado de resposta: 48 horas úteis ou menos</span>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FreeLandingPageBRThankYou;